/**
 * Mock data for the price-group admin journeys.
 *
 * A central "Standard pricing" policy (no commitment) plus a "Ramsay" customer
 * price group (with commitment + a key-account market override). Mirrors
 * docs/features/product-catalogue/03_pricing_model.md. Locally typed for
 * journey iteration; productionises to the pricing module's API later.
 */

import type { PriceGroup } from "../data/pricing-admin-types";
import {
  MOCK_BASELINE_SPEED,
  MOCK_SPEED_LADDER,
  MOCK_SPEED_PRICE_RULE,
} from "./speeds";

const standardPricing: PriceGroup = {
  id: "standard",
  name: "Standard pricing",
  scope: "central",
  versions: [
    {
      v: 3,
      state: "live",
      by: "A. Patel",
      when: "12 Jun, 09:42",
      note: "Q3 capacity + market",
    },
    {
      v: 2,
      state: "superseded",
      by: "J. Doe",
      when: "01 Apr, 14:08",
      range: "01 Apr–12 Jun",
    },
    {
      v: 1,
      state: "superseded",
      by: "J. Doe",
      when: "01 Jan, 11:20",
      range: "01 Jan–01 Apr",
    },
  ],
  // Fractions: 0.3 = 30%. Bought-in null = the same as in-house.
  commercial: {
    contributionMarginPct: 0.3,
    boughtInContributionMarginPct: null,
  },
  // The Speed price rule (d65) over Time's ladder; the ladder is read-only.
  speed: {
    rule: MOCK_SPEED_PRICE_RULE,
    ladder: MOCK_SPEED_LADDER,
    baselineCode: MOCK_BASELINE_SPEED,
  },
  commitment: null,
  market: {
    enabled: true,
    defaultStance: { stance: "track" },
    applyMode: "suggest",
    minConfidence: "med",
    exceptions: [
      {
        itemId: "standard-folder",
        itemLabel: "Presentation folder",
        stance: "hold",
      },
      {
        itemId: "standard-business-card",
        itemLabel: "Business card",
        stance: "win",
        dial: 0.05,
      },
    ],
  },
  capacity: {
    enabled: true,
    defaultBands: [
      { upTo: 0.7, kind: "none", pct: 0 },
      { upTo: 1.0, kind: "premium", pct: 0.1 },
    ],
    overrides: [
      {
        area: "Finishing",
        bands: [
          { upTo: 0.5, kind: "discount", pct: 0.1 },
          { upTo: 0.8, kind: "none", pct: 0 },
          { upTo: 1.0, kind: "premium", pct: 0.15 },
        ],
      },
    ],
  },
  guardrails: {
    rounding: "none",
    rows: [
      {
        name: "Contribution",
        description:
          "Margin as a % of price: base price = cost ÷ (1 − contribution).",
        approval: "8",
        hard: "0",
      },
      {
        name: "Total concession",
        description: "The whole give-back combined: discount + rebate.",
        approval: "12",
        hard: "18",
      },
      {
        name: "Repeat-price move",
        description:
          "The maximum change vs what this customer last paid for the same spec (±10% quantity, 6 months). Wider upward than down: real repricing moves in +8–10% steps.",
        directional: true,
        // Calibrated on the validation distribution (docs/features/
        // price-precedent/00_spec_key_validation.md), not the design's 5/15.
        approval: "10",
        approvalDown: "8",
        hard: "25",
        hardDown: "20",
      },
    ],
  },
  // 5% matches Tharstern's own carriage-markup practice for the shared policy.
  precedent: {
    suggestLastOrdered: true,
    windowMonths: 6,
    quantityTolerancePct: 10,
    floor: "both",
  },
  carriage: { markupPct: 0.05 },
};

const ramsayPricing: PriceGroup = {
  id: "ramsay",
  name: "Ramsay",
  scope: { customerName: "Ramsay Healthcare" },
  versions: [
    {
      v: 3,
      state: "live",
      by: "A. Patel",
      when: "02 Jun, 16:15",
      note: "Framework renewal",
    },
    {
      v: 2,
      state: "superseded",
      by: "A. Patel",
      when: "12 Jan, 10:03",
      range: "12 Jan–02 Jun",
    },
    {
      v: 1,
      state: "superseded",
      by: "M. Cole",
      when: "03 Jun '25, 09:30",
      range: "03 Jun '25–12 Jan",
    },
  ],
  // Fractions: 0.3 = 30%. Bought-in null = the same as in-house.
  commercial: {
    contributionMarginPct: 0.3,
    boughtInContributionMarginPct: null,
  },
  speed: standardPricing.speed,
  commitment: {
    discountDefaultPct: 0.05,
    discountExceptions: [
      { productType: "Business Cards", pct: 0.08 },
      { productType: "Large Format", pct: 0.03 },
    ],
    rebate: {
      period: "Contract year",
      bands: [
        { upTo: 500000, pct: 0 },
        { upTo: 850000, pct: 0.03 },
        { upTo: null, pct: 0.05 },
      ],
    },
  },
  market: {
    enabled: true,
    defaultStance: { stance: "track" },
    applyMode: "suggest",
    minConfidence: "med",
    exceptions: [
      {
        itemId: "ramsay-business-card",
        itemLabel: "Business card",
        stance: "premium",
        dial: 0.03,
      },
    ],
  },
  capacity: standardPricing.capacity,
  guardrails: standardPricing.guardrails,
  precedent: standardPricing.precedent,
  // Negotiated pass-through — this account's own price group overrides the
  // shared 5% back off.
  carriage: null,
};

const priceGroups: PriceGroup[] = [standardPricing, ramsayPricing];

export function getMockPriceGroups(): PriceGroup[] {
  return priceGroups;
}

export function getMockPriceGroup(id: string): PriceGroup | null {
  return priceGroups.find((r) => r.id === id) ?? null;
}
